<template>
  <div class="flatfinder-frames">
    <div class="flatfinder-frames__stage">
      <Draggable
        :value="value"
        :disabled="selectMode"
        draggable=".flatfinder-frames__item"
        class="flatfinder-frames__items"
        @input="reorder"
      >
        <div
          v-for="(frame, index) in value"
          :key="$path('image.url', frame) || index"
          class="flatfinder-frames__thumb flatfinder-frames__item"
          :class="{
            'flatfinder-frames__thumb--active': index === current && !selectMode,
            'flatfinder-frames__thumb--selected': selectMode && isSelected(frame),
            'flatfinder-frames__thumb--dragover': dragTarget === index && dragValid,
            'flatfinder-frames__thumb--dragover-invalid': dragTarget === index && !dragValid,
          }"
          role="button"
          tabindex="0"
          @click="selectMode ? toggleSelect(frame) : $emit('update:current', index)"
          @keypress.enter="selectMode ? toggleSelect(frame) : $emit('update:current', index)"
          @dragover="onTileDragOver($event, index)"
          @dragleave="onTileDragLeave($event, index)"
          @drop="onTileDrop($event, index)"
        >
          <img :src="$path('image.url', frame)" :alt="`${index + 1}`" loading="lazy" decoding="async" />
          <span class="flatfinder-frames__badges">
            <span
              v-if="(frame.items || []).length"
              class="flatfinder-frames__badge flatfinder-frames__badge--items"
              v-tooltip="$t('selectedUnits')"
            >
              <Icon :icon="['fal', 'draw-polygon']" />
              {{ (frame.items || []).length }}
            </span>
            <span
              v-if="(frame.sunlightConditions || []).length"
              class="flatfinder-frames__badge flatfinder-frames__badge--sunlight"
              v-tooltip="$t('sunlightConditions')"
            >
              <Icon :icon="['fal', 'sun']" />
              {{ (frame.sunlightConditions || []).length }}
            </span>
            <span
              v-if="frame.cardinalDirection != null"
              class="flatfinder-frames__badge flatfinder-frames__badge--direction"
              v-tooltip="directionTooltip(frame)"
            >
              <Icon :icon="['fas', 'arrow-up']" :style="{ transform: `rotate(${frame.cardinalDirection}deg)` }" />
            </span>
          </span>
          <span class="flatfinder-frames__number">{{ index + 1 }}</span>
          <span
            v-if="selectMode"
            class="flatfinder-frames__check overlay-icon"
            :class="{ 'flatfinder-frames__check--checked': isSelected(frame) }"
          >
            <Icon v-if="isSelected(frame)" :icon="['fal', 'check']" />
          </span>
          <span v-if="!selectMode" class="flatfinder-frames__actions">
            <button
              v-if="value.length > 1"
              type="button"
              class="overlay-icon overlay-icon--error"
              v-tooltip="$tc('deleteFrame', 1)"
              @click.stop="askDelete(index)"
            >
              <Icon icon="trash" />
            </button>
            <button type="button" class="overlay-icon" v-tooltip="$t('changeImage')" @click.stop="pickReplace(index)">
              <Icon :icon="['fal', 'sync']" />
            </button>
            <button
              type="button"
              class="overlay-icon overlay-icon--primary"
              v-tooltip="$t('download')"
              @click.stop="download(frame)"
            >
              <Icon icon="arrow-alt-circle-down" />
            </button>
          </span>
        </div>

        <div
          slot="footer"
          class="flatfinder-frames__thumb flatfinder-frames__thumb--upload"
          :class="{
            'flatfinder-frames__thumb--uploading': uploading,
            'flatfinder-frames__thumb--disabled': selectMode,
            'flatfinder-frames__thumb--dragover': dragTarget === 'upload' && dragValid,
            'flatfinder-frames__thumb--dragover-invalid': dragTarget === 'upload' && !dragValid,
          }"
          role="button"
          tabindex="0"
          @click="pick"
          @keypress.enter="pick"
          @dragover="onTileDragOver($event, 'upload')"
          @dragleave="onTileDragLeave($event, 'upload')"
          @drop="onTileDrop($event, 'upload')"
        >
          <Icon :icon="['fal', 'arrow-to-top']" />
          <span>{{ $t('upload') }}</span>
          <span v-if="dimensionsHint" class="flatfinder-frames__hint">{{ dimensionsHint }}</span>
        </div>
      </Draggable>

      <ButtonComponent
        v-if="!selectMode && value.length"
        class="flatfinder-frames__select-trigger"
        :label="$t('select')"
        :icon="['fal', 'object-group']"
        @click="enterSelectMode"
      />

      <transition name="flatfinder-frames__toolbar">
        <div v-if="selectMode" class="flatfinder-frames__toolbar">
          <ButtonComponent
            :label="allSelected ? $t('deselectAll') : $t('selectAll')"
            :icon="['fal', allSelected ? 'minus-square' : 'check-square']"
            @click="toggleSelectAll"
          />
          <ButtonComponent
            theme="error"
            :class="{ 'elder-button--disabled': !selectedCount }"
            :label="deleteSelectedLabel"
            :icon="['fal', 'trash']"
            @click="selectedCount && (showDeleteSelected = true)"
          />
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="exitSelectMode" />
        </div>
      </transition>
    </div>

    <UploadProgress v-if="uploading" :value="uploadProgress" class="flatfinder-frames__progress" />

    <div v-if="error" class="flatfinder-frames__notification">{{ error.message }}</div>

    <ModalComponent :show="deleteIndex !== null" @close="deleteIndex = null" class="flatfinder-frames__modal">
      <Card theme="flat">
        <template #header>
          <FontAwesomeIcon :icon="['fal', 'exclamation-triangle']" size="2x" />
          <strong class="mt-1">{{ $tc('deleteFrame', 1) }}</strong>
        </template>
        <template #default>
          <p>
            {{
              $t('deleteFrameDescription', {
                units: (deleteTarget.items || []).length,
                sunlight: (deleteTarget.sunlightConditions || []).length,
              })
            }}
          </p>
        </template>
        <template #footer>
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="deleteIndex = null" />
          <ButtonComponent theme="error" :label="$t('delete')" :icon="['fal', 'trash']" @click="confirmDelete" />
        </template>
      </Card>
    </ModalComponent>

    <ConfirmDialog
      :show="showDeleteSelected"
      :title="$tc('deleteFrame', selectedCount)"
      :message="$tc('deleteFramesDescription', selectedCount, { count: selectedCount })"
      :confirmLabel="$t('delete')"
      :confirmIcon="['fal', 'trash']"
      @confirm="confirmDeleteSelected"
      @cancel="showDeleteSelected = false"
    />
  </div>
</template>

<script>
import { ButtonComponent } from 'vue-elder-button'
import { ModalComponent } from 'vue-elder-modal'
import Draggable from 'vuedraggable'
import Card from '@/components/Card'
import ConfirmDialog from '@/components/ConfirmDialog'
import { DownloadUrl } from '@/utils'
import { formatDimensions } from './dimensions'
import { directionPoint } from './directions'
import { pickFiles, uploadImages } from './upload'
import { seriesOrder } from './conditions'
import TileDropzone from './tileDropzone.mixin'
import UploadProgress from './UploadProgress.vue'

export default {
  mixins: [TileDropzone],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    current: Number,
  },
  data() {
    return {
      uploading: false,
      uploadProgress: 0,
      error: null,
      deleteIndex: null,
      selectMode: false,
      selected: [],
      showDeleteSelected: false,
    }
  },
  computed: {
    dropDisabled() {
      return this.selectMode
    },
    dimensionsHint() {
      return formatDimensions(this.value)
    },
    deleteTarget() {
      return this.value[this.deleteIndex] || {}
    },
    selectedCount() {
      return this.selected.length
    },
    allSelected() {
      return this.value.length > 0 && this.selected.length === this.value.length
    },
    deleteSelectedLabel() {
      const label = this.$t('deleteSelected')
      return this.selectedCount ? `${label} (${this.selectedCount})` : label
    },
  },
  methods: {
    pick() {
      if (this.selectMode) return
      pickFiles({ multiple: true }).then(files => {
        if (!files.length) return
        this.addFrames(files)
      })
    },
    dropAccepts(target, count) {
      return target === 'upload' || count === 1
    },
    onDropFiles(target, files) {
      if (target === 'upload') this.addFrames(files)
      else if (files.length === 1) this.replaceFrameImage({ index: target, files })
    },
    pickReplace(index) {
      pickFiles().then(files => {
        if (!files.length) return
        this.replaceFrameImage({ index, files })
      })
    },
    formatUploadError(raw) {
      if (!raw) return null
      return { message: raw.count ? this.$tc('uploadImageDimensionsMismatch', raw.count, raw) : raw.message }
    },
    addFrames(files) {
      if (!files.length) return

      this.uploading = true
      this.uploadProgress = 0
      this.error = null

      return uploadImages(seriesOrder(files), this.value, p => (this.uploadProgress = p))
        .then(({ accepted, error }) => {
          this.error = this.formatUploadError(error)
          if (!accepted.length) return

          const start = this.value.length
          this.$emit('input', [
            ...this.value,
            ...accepted.map(image => ({ image, cardinalDirection: null, items: [], sunlightConditions: [] })),
          ])
          this.$emit('update:current', start)
        })
        .finally(() => (this.uploading = false))
    },
    replaceFrameImage({ index, files }) {
      if (!this.value[index] || !files.length) return

      this.uploading = true
      this.uploadProgress = 0
      this.error = null

      return uploadImages(files.slice(0, 1), this.value.length > 1 ? this.value : [], p => (this.uploadProgress = p))
        .then(({ accepted, error }) => {
          this.error = this.formatUploadError(error)

          const image = accepted[0]
          if (!image) return
          this.$emit(
            'input',
            this.value.map((frame, i) => (i === index ? { ...frame, image } : frame)),
          )
        })
        .finally(() => (this.uploading = false))
    },
    download(frame) {
      if (!frame.image) return
      DownloadUrl(frame.image.url, frame.image.name)
    },
    directionTooltip(frame) {
      const point = directionPoint(frame.cardinalDirection)
      return `${this.$t(`cardinalDirection${point}`)} · ${Math.round(frame.cardinalDirection)}°`
    },
    askDelete(index) {
      this.deleteIndex = index
    },
    confirmDelete() {
      this.deleteFrame(this.deleteIndex)
      this.deleteIndex = null
    },

    setFrames(newFrames, previousFrame) {
      this.$emit('input', newFrames)
      const index = newFrames.indexOf(previousFrame)
      this.$emit('update:current', index >= 0 ? index : Math.min(this.current, Math.max(newFrames.length - 1, 0)))
    },
    deleteFrame(index) {
      this.setFrames(
        this.value.filter((_, i) => i !== index),
        this.value[this.current],
      )
    },
    enterSelectMode() {
      this.selectMode = true
    },
    exitSelectMode() {
      this.selectMode = false
      this.selected = []
    },
    isSelected(frame) {
      return this.selected.includes(frame)
    },
    toggleSelect(frame) {
      const index = this.selected.indexOf(frame)
      if (index === -1) this.selected.push(frame)
      else this.selected.splice(index, 1)
    },
    toggleSelectAll() {
      this.selected = this.allSelected ? [] : this.value.slice()
    },
    confirmDeleteSelected() {
      this.setFrames(
        this.value.filter(frame => !this.selected.includes(frame)),
        this.value[this.current],
      )
      this.exitSelectMode()
      this.showDeleteSelected = false
    },
    reorder(newFrames) {
      this.setFrames(newFrames, this.value[this.current])
    },
  },
  components: {
    ButtonComponent,
    ModalComponent,
    Draggable,
    Card,
    ConfirmDialog,
    UploadProgress,
  },
}
</script>

<style lang="scss">
.flatfinder-frames {
  position: relative;

  &__stage {
    position: relative;

    .elder-button--default {
      background-color: white;
    }

    @media (hover: hover) {
      &:hover .flatfinder-frames__select-trigger {
        opacity: 1;
        transform: none;
        pointer-events: auto;
      }
    }
  }

  &__select-trigger,
  &__toolbar {
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 2;
  }

  &__select-trigger {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);

    @media (hover: hover) {
      opacity: 0;
      transform: translateY(4px);
      transition: opacity 150ms ease-out, transform 150ms ease-out;
      pointer-events: none;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .elder-button {
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    }

    .elder-button--disabled {
      color: var(--vue-elder-button-disabled-contrast, #737373) !important;
      border-color: var(--vue-elder-button-disabled, #ccc) !important;
      background-color: var(--vue-elder-button-disabled, #ccc) !important;
    }

    .elder-button--error:not(.elder-button--disabled) {
      color: var(--error) !important;
      border-color: transparent !important;
      background-color: white !important;
      transition: color 0.15s, background-color 0.15s;

      &:hover {
        color: var(--error-contrast) !important;
        background-color: var(--error) !important;
      }
    }
  }

  &__toolbar-enter,
  &__toolbar-leave-to {
    opacity: 0;
    transform: translateY(4px);
  }

  &__toolbar-enter-active,
  &__toolbar-leave-active {
    transition: opacity 150ms ease-out, transform 150ms ease-out;
  }

  &__progress {
    margin-top: 0.75rem;
  }

  &__items {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 0.75rem;
    padding: 2px;
  }

  &__item#{&}__thumb {
    cursor: grab;

    &.sortable-chosen {
      cursor: grabbing;
    }
  }

  &__thumb {
    position: relative;
    aspect-ratio: 16 / 10;
    border-radius: var(--border-radius-sm);
    cursor: pointer;
    background-color: var(--light-grey);
    content-visibility: auto;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      border-radius: inherit;
    }

    &--active,
    &--selected {
      outline: 2px solid var(--primary);
      outline-offset: 2px;
    }

    &--dragover {
      outline: 2px dashed var(--primary);
      outline-offset: 2px;
    }

    &--dragover-invalid {
      outline: 2px dashed var(--error);
      outline-offset: 2px;
    }

    @media (hover: hover) {
      &:hover .flatfinder-frames__actions {
        opacity: 1;
        transform: translateY(0);
      }
    }

    &--upload {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      border: 1px dashed var(--grey);
      background-color: white;
      color: var(--dark-grey);
      font-size: 0.8em;

      &:hover {
        background-color: #f8f8f8;
      }
    }

    &--uploading,
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__badges {
    position: absolute;
    top: 4px;
    left: 4px;
    display: flex;
    gap: 4px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    color: white;
    font-size: 0.7em;
    cursor: pointer;

    &--items {
      background-color: var(--primary);
    }

    &--sunlight {
      background-color: rgba(0, 0, 0, 0.55);
    }

    &--direction {
      background-color: rgba(0, 0, 0, 0.55);

      .kvass-icon {
        transition: transform 200ms ease;
      }
    }
  }

  &__number {
    position: absolute;
    bottom: 4px;
    left: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background-color: rgba(0, 0, 0, 0.55);
    color: white;
    font-size: 0.75em;
    font-weight: 700;
  }

  &__check {
    position: absolute;
    top: 6px;
    right: 6px;
    background-color: rgba(0, 0, 0, 0.35);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.7);

    &--checked {
      background-color: var(--primary);
      box-shadow: none;
    }
  }

  &__actions {
    position: absolute;
    top: -5px;
    right: -5px;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    transition: opacity 150ms ease-out, transform 150ms ease-out;

    @media (hover: hover) {
      opacity: 0;
      transform: translateY(50%);
    }
  }

  &__hint {
    font-size: 0.85em;
    color: var(--grey);
  }

  &__notification {
    margin-top: 0.75rem;
    padding: 0.85rem 1.1rem;
    border-radius: var(--border-radius-sm);
    background-color: css-alpha('error', 0.08);
    font-size: 0.9em;
  }

  &__modal {
    .card__footer-content {
      justify-content: flex-end;
    }
  }
}
</style>
